"use client";

import { useState, useMemo, useCallback, useEffect } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { CATALOG, type Product } from "@/lib/catalog";
import { RatingStars } from "@/components/RatingStars";
import { ProductImage } from "@/components/ProductImage";
import { ProgressRing } from "@/components/ProgressRing";
import {
  startJob,
  getJob,
  subscribe,
  getSnapshot,
  getServerSnapshot,
  hydrate,
  type TryOnRequest,
} from "@/lib/tryOnJobs";
import { useSyncExternalStore } from "react";
import { formatPrice, CURRENCY_CODES, getCurrencySymbol } from "@/lib/currency";

/* ── Filters ───────────────────────────────────────────────── */
const CATEGORIES = ["All", "Tops", "Shirts", "Dresses", "Jackets", "Hoodies", "Pants", "Activewear"];
const MATERIALS = ["Linen", "Cotton", "Polyester", "Spandex", "Rayon", "Chiffon", "Nylon", "Cotton Blend", "Satin"];
const SORT_OPTIONS = [
  { value: "featured", label: "Featured" },
  { value: "price-low", label: "Price: Low to High" },
  { value: "price-high", label: "Price: High to Low" },
  { value: "rating", label: "Top Rated" },
  { value: "newest", label: "Newest" },
];

interface Filters {
  category: string;
  priceMin: number;
  priceMax: number;
  colors: string[];
  fit: string;
  size: string;
  materials: string[];
  search: string;
}

const DEFAULT_FILTERS: Filters = {
  category: "All",
  priceMin: 0,
  priceMax: 200,
  colors: [],
  fit: "",
  size: "",
  materials: [],
  search: "",
};

/* ── DB Product type (from API) ────────────────────────────── */
interface DBProduct {
  id: string;
  name: string;
  slug?: string;
  brand_name?: string;
  price: number;
  currency?: string;
  category: string;
  sizes: string[];
  colors: { name: string; hex: string }[];
  materials: string[];
  fits: string[];
  images?: { url: string; alt?: string; sort_order?: number }[];
  rating?: number;
  reviews_count?: number;
}

/* ── Page ──────────────────────────────────────────────────── */
export default function CatalogPage() {
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const router = useRouter();
  const [sort, setSort] = useState("featured");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [savedIds, setSavedIds] = useState<string[]>([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Currency state
  const [currency, setCurrency] = useState<string>("TTD");

  // DB products state
  const [dbProducts, setDbProducts] = useState<Product[]>([]);
  const [loadingDb, setLoadingDb] = useState(true);

  // Load currency preference
  useEffect(() => {
    const saved = localStorage.getItem("dr-currency");
    if (saved && CURRENCY_CODES.includes(saved)) setCurrency(saved);
  }, []);

  const handleCurrencyChange = (code: string) => {
    setCurrency(code);
    localStorage.setItem("dr-currency", code);
  };

  // Fetch from DB on mount
  useEffect(() => {
    async function fetchCatalog() {
      try {
        const res = await fetch("/api/catalog?limit=100");
        if (!res.ok) throw new Error("API error");
        const data = await res.json();
        if (data.products && data.products.length > 0) {
          const mapped: Product[] = data.products.map((p: DBProduct) => ({
            id: p.id,
            name: p.name,
            brand: p.brand_name || "Unknown",
            category: p.category,
            price: Number(p.price),
            rating: Number(p.rating) || 0,
            reviews: Number(p.reviews_count) || 0,
            colors: Array.isArray(p.colors) ? p.colors : [],
            sizes: Array.isArray(p.sizes) ? p.sizes : ["M"],
            materials: Array.isArray(p.materials) ? p.materials : [],
            fits: Array.isArray(p.fits) ? p.fits : ["Regular"],
            images: Array.isArray(p.images) ? p.images : [],
          }));
          setDbProducts(mapped);
        }
      } catch {
        // Fallback: keep empty
      } finally {
        setLoadingDb(false);
      }
    }
    fetchCatalog();
  }, []);

  // Merge DB products with static catalog
  const allProducts = useMemo(() => {
    if (dbProducts.length > 0) {
      return dbProducts;
    }
    return CATALOG;
  }, [dbProducts]);

  // Load saved IDs from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem("dressingroom-saved");
      if (saved) setSavedIds(JSON.parse(saved));
    } catch { /* ignore */ }
  }, []);

  // Try-on panel state
  const [selSize, setSelSize] = useState("");
  const [selColorIdx, setSelColorIdx] = useState(0);
  const [selFit, setSelFit] = useState("Regular");

  // Job store
  const jobStore = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Get all unique colors from products
  const allColors = useMemo(() => {
    const map = new Map<string, string>();
    allProducts.forEach((p) => p.colors.forEach((c) => map.set(c.hex, c.name)));
    return Array.from(map, ([hex, name]) => ({ hex, name }));
  }, [allProducts]);

  // Filter products
  const filtered = useMemo(() => {
    let result = allProducts.filter((p) => {
      if (filters.category !== "All" && p.category !== filters.category) return false;
      if (p.price < filters.priceMin || p.price > filters.priceMax) return false;
      if (filters.search && !p.name.toLowerCase().includes(filters.search.toLowerCase()) && !p.brand.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.fit && !p.fits.includes(filters.fit as any)) return false;
      if (filters.size && !p.sizes.includes(filters.size as any)) return false;
      if (filters.materials.length > 0) {
        const hasMat = p.materials.some((m) => filters.materials.some((fm) => m.includes(fm)));
        if (!hasMat) return false;
      }
      if (filters.colors.length > 0) {
        const hasColor = p.colors.some((c) => filters.colors.includes(c.hex));
        if (!hasColor) return false;
      }
      return true;
    });

    switch (sort) {
      case "price-low": result = [...result].sort((a, b) => a.price - b.price); break;
      case "price-high": result = [...result].sort((a, b) => b.price - a.price); break;
      case "rating": result = [...result].sort((a, b) => b.rating - a.rating); break;
      case "newest": break;
    }

    return result;
  }, [filters, sort, allProducts]);

  function updateFilter<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function toggleColor(hex: string) {
    setFilters((prev) => ({
      ...prev,
      colors: prev.colors.includes(hex)
        ? prev.colors.filter((c) => c !== hex)
        : [...prev.colors, hex],
    }));
  }

  function toggleMaterial(mat: string) {
    setFilters((prev) => ({
      ...prev,
      materials: prev.materials.includes(mat)
        ? prev.materials.filter((m) => m !== mat)
        : [...prev.materials, mat],
    }));
  }

  function resetFilters() {
    setFilters(DEFAULT_FILTERS);
  }

  function selectProduct(product: Product) {
    setSelectedProduct(product);
    setSelSize(product.sizes[0] || "");
    setSelColorIdx(0);
    setSelFit(product.fits[0] || "Regular");
    setRightPanelOpen(true);

    const req = buildRequest(product);
    if (req) hydrate(req);
  }

  function isSaved(id: string): boolean {
    return savedIds.includes(id);
  }

  function toggleSave(id: string) {
    const updated = isSaved(id)
      ? savedIds.filter((s) => s !== id)
      : [...savedIds, id];
    setSavedIds(updated);
    localStorage.setItem("dressingroom-saved", JSON.stringify(updated));
  }

  function buildRequest(product: Product): TryOnRequest | null {
    const profileStr = typeof window !== "undefined" ? localStorage.getItem("dressingroom-profile") : null;
    if (!profileStr) return null;
    try {
      const profile = JSON.parse(profileStr);
      return {
        productId: product.id,
        productName: product.name,
        size: selSize || product.sizes[0],
        color: product.colors[selColorIdx]?.name || "",
        colorHex: product.colors[selColorIdx]?.hex || "",
        fit: selFit || product.fits[0] || "Regular",
        profile,
      };
    } catch {
      return null;
    }
  }

  const currentJob = selectedProduct ? (() => {
    const req = buildRequest(selectedProduct);
    if (!req) return undefined;
    const key = [req.productId, req.size, req.color, req.fit].join("|");
    return jobStore.get(key);
  })() : undefined;

  async function handleGenerate() {
    if (!selectedProduct) return;
    const req = buildRequest(selectedProduct);
    if (!req) return;
    await startJob(req);
  }

  // WhatsApp share
  const waShareUrl = useMemo(() => {
    if (currentJob?.status === "done" && currentJob.result?.imageUrl) {
      const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
      const imgUrl = currentJob.result.imageUrl.startsWith("http")
        ? currentJob.result.imageUrl
        : `${baseUrl}${currentJob.result.imageUrl}`;
      const text = encodeURIComponent(`Check out how this looks on me on DressingRoom! ${imgUrl}`);
      return `https://wa.me/?text=${text}`;
    }
    return "";
  }, [currentJob]);

  function copyShareLink() {
    if (currentJob?.status === "done" && currentJob.result?.imageUrl) {
      const baseUrl = typeof window !== "undefined" ? window.location.origin : "";
      const imgUrl = currentJob.result.imageUrl.startsWith("http")
        ? currentJob.result.imageUrl
        : `${baseUrl}${currentJob.result.imageUrl}`;
      navigator.clipboard?.writeText(`${baseUrl}/catalog?product=${selectedProduct?.id}`).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  }

  function formatProductPrice(price: number): string {
    return formatPrice(price, "TTD", currency);
  }

  return (
    <>
      <header className="site-header">
        <Link href="/" className="site-header__brand">DressingRoom</Link>
        <nav className="site-header__nav">
          <Link href="/profile" className="site-header__link">Profile</Link>
          <Link href="/store" className="site-header__link">Stores</Link>
          <Link href="/catalog" className="site-header__link site-header__link--active">Catalog</Link>
          <Link href="/saved" className="site-header__link">Saved</Link>
          {/* Currency selector */}
          <div className="currency-selector">
            <select
              value={currency}
              onChange={(e) => handleCurrencyChange(e.target.value)}
              className="currency-selector__select"
              aria-label="Select currency"
            >
              {CURRENCY_CODES.map((code) => (
                <option key={code} value={code}>
                  {getCurrencySymbol(code)} {code}
                </option>
              ))}
            </select>
          </div>
        </nav>
        {/* Mobile currency selector */}
        <div className="currency-selector currency-selector--mobile">
          <select
            value={currency}
            onChange={(e) => handleCurrencyChange(e.target.value)}
            className="currency-selector__select"
            aria-label="Currency"
          >
            {CURRENCY_CODES.map((code) => (
              <option key={code} value={code}>{getCurrencySymbol(code)}</option>
            ))}
          </select>
        </div>
      </header>

      <div className="app-shell">
        {/* ── Left Sidebar (Filters) ──────────────────────── */}
        <aside className={`app-shell__left ${mobileFiltersOpen ? "app-shell__left--mobile-open" : ""}`}>
          <div className="filter-group">
            <h3 className="filter-group__title">Category</h3>
            <div className="filter-list">
              {CATEGORIES.map((cat) => (
                <div
                  key={cat}
                  className={`filter-item ${filters.category === cat ? "filter-item--active" : ""}`}
                  onClick={() => updateFilter("category", cat)}
                >
                  {cat}
                  <span className="filter-item__count">
                    {cat === "All" ? allProducts.length : allProducts.filter((p) => p.category === cat).length}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3 className="filter-group__title">Price Range</h3>
            <div className="range-row">
              <input
                type="range"
                min="0"
                max="200"
                step="5"
                value={filters.priceMax}
                onChange={(e) => updateFilter("priceMax", Number(e.target.value))}
              />
              <span className="range-value">{getCurrencySymbol(currency)}{filters.priceMax}</span>
            </div>
          </div>

          <div className="filter-group">
            <h3 className="filter-group__title">Color</h3>
            <div className="filter-color-row">
              {allColors.map((c) => (
                <div
                  key={c.hex}
                  className={`filter-color-dot ${filters.colors.includes(c.hex) ? "filter-color-dot--active" : ""}`}
                  style={{ background: c.hex }}
                  title={c.name}
                  onClick={() => toggleColor(c.hex)}
                />
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3 className="filter-group__title">Fit</h3>
            <div className="filter-list">
              {["", "Slim", "Regular", "Oversized"].map((f) => (
                <div
                  key={f || "all"}
                  className={`filter-item ${filters.fit === f ? "filter-item--active" : ""}`}
                  onClick={() => updateFilter("fit", f)}
                >
                  {f || "All Fits"}
                </div>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3 className="filter-group__title">Size</h3>
            <div className="filter-list" style={{ flexDirection: "row", flexWrap: "wrap", gap: "var(--sp-1)" }}>
              <div
                className={`filter-item ${filters.size === "" ? "filter-item--active" : ""}`}
                onClick={() => updateFilter("size", "")}
                style={{ marginRight: "var(--sp-2)" }}
              >
                All
              </div>
              {["XS", "S", "M", "L", "XL", "XXL"].map((s) => (
                <div
                  key={s}
                  className={`filter-item ${filters.size === s ? "filter-item--active" : ""}`}
                  onClick={() => updateFilter("size", s)}
                  style={{ padding: "2px 8px", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}
                >
                  {s}
                </div>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <h3 className="filter-group__title">Material</h3>
            <div className="filter-list">
              {MATERIALS.map((m) => (
                <label key={m} className="check-row">
                  <input
                    type="checkbox"
                    checked={filters.materials.includes(m)}
                    onChange={() => toggleMaterial(m)}
                  />
                  {m}
                </label>
              ))}
            </div>
          </div>

          <button className="button button--sm button--secondary button--block" onClick={resetFilters}>
            Reset Filters
          </button>
        </aside>

        {/* Mobile filter backdrop */}
        {mobileFiltersOpen && (
          <div className="filter-backdrop" onClick={() => setMobileFiltersOpen(false)} />
        )}

        {/* ── Main Area ────────────────────────────────────── */}
        <main className="app-shell__main">
          <div className="catalog-header">
            {/* Mobile filter toggle */}
            <button
              className="mobile-filter-toggle"
              onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 4h12M4 8h8M6 12h4" strokeLinecap="round" />
              </svg>
              Filters
            </button>
            <div className="catalog-search">
              <svg className="catalog-search__icon" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <circle cx="7" cy="7" r="5" /><path d="M11 11l3 3" strokeLinecap="round" />
              </svg>
              <input
                type="text"
                placeholder="Search products or brands…"
                value={filters.search}
                onChange={(e) => updateFilter("search", e.target.value)}
              />
            </div>
            <div className="select-box catalog-sort">
              <select value={sort} onChange={(e) => setSort(e.target.value)}>
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div className="catalog-results-count">
            {loadingDb ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "Product" : "Products"}`}
          </div>

          {loadingDb ? (
            <div className="empty-state">
              <h3 className="empty-state__title">Loading products…</h3>
            </div>
          ) : filtered.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-state__title">No Products Found</h3>
              <p className="empty-state__text">
                Try adjusting your filters or search terms to find what you'"'"'re looking for.
              </p>
              <button className="button button--secondary" onClick={resetFilters}>
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="product-grid">
              {filtered.map((product) => (
                <div
                  key={product.id}
                  className={`product-card ${selectedProduct?.id === product.id ? "product-card--selected" : ""}`}
                  onClick={() => router.push(`/product/${product.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <ProductImage
                    productId={product.id}
                    alt={product.name}
                    className="product-card__image" src={product.images?.[0]?.url} />
                  <div className="product-card__body">
                    <div className="product-card__name">{product.name}</div>
                    <div className="product-card__brand">{product.brand}</div>
                    <div className="product-card__price">{formatProductPrice(product.price)}</div>
                    <div className="product-card__footer">
                      <RatingStars rating={product.rating} reviews={product.reviews} />
                      <button
                        className="product-card__tryon-btn"
                        onClick={(e) => { e.stopPropagation(); selectProduct(product); }}
                      >
                        Try On
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>

        {/* ── Right Panel (Try-On Preview) ─────────────────── */}
        {rightPanelOpen && selectedProduct && (
          <div className="right-panel-backdrop right-panel-backdrop--show" onClick={() => setRightPanelOpen(false)} />
        )}
        <aside className={`app-shell__right ${rightPanelOpen ? "app-shell__right--open" : ""}`}>
          <div className="right-panel">
            {selectedProduct ? (
              <>
                <div className="right-panel__section">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--sp-4)" }}>
                    <h3 className="right-panel__title" style={{ margin: 0 }}>Try-On Preview</h3>
                    <button
                      className="button button--sm button--secondary"
                      onClick={() => setRightPanelOpen(false)}
                      style={{ minWidth: "32px", padding: "0 8px" }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Product summary */}
                  <div style={{ display: "flex", gap: "var(--sp-3)", marginBottom: "var(--sp-4)" }}>
                    <div style={{ width: 64, height: 80, flexShrink: 0, background: "var(--surface-alt)", borderRadius: "var(--radius)", overflow: "hidden" }}>
                      <ProductImage productId={selectedProduct.id} alt={selectedProduct.name} className="" src={selectedProduct.images?.[0]?.url} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="product-card__name">{selectedProduct.name}</div>
                      <div className="product-card__brand">{selectedProduct.brand}</div>
                      <div className="product-card__price" style={{ marginTop: "2px" }}>{formatProductPrice(selectedProduct.price)}</div>
                    </div>
                  </div>
                </div>

                <div className="right-panel__section">
                  {/* Size selector */}
                  <div className="selector-row">
                    <span className="selector-label">Size</span>
                    <div className="selector-pills">
                      {selectedProduct.sizes.map((s) => (
                        <div
                          key={s}
                          className={`selector-pill ${selSize === s ? "selector-pill--active" : ""}`}
                          onClick={() => setSelSize(s)}
                        >
                          {s}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Color selector */}
                  <div className="selector-row">
                    <span className="selector-label">Color — {selectedProduct.colors[selColorIdx]?.name}</span>
                    <div className="color-selector">
                      {selectedProduct.colors.map((c, i) => (
                        <div
                          key={c.hex}
                          className={`color-selector__dot ${selColorIdx === i ? "color-selector__dot--active" : ""}`}
                          style={{ background: c.hex }}
                          title={c.name}
                          onClick={() => setSelColorIdx(i)}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Fit selector */}
                  <div className="selector-row">
                    <span className="selector-label">Fit</span>
                    <div className="selector-pills">
                      {selectedProduct.fits.map((f) => (
                        <div
                          key={f}
                          className={`selector-pill ${selFit === f ? "selector-pill--active" : ""}`}
                          onClick={() => setSelFit(f)}
                        >
                          {f}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: "var(--sp-2)", marginTop: "var(--sp-4)" }}>
                    <button
                      className="button button--primary"
                      onClick={handleGenerate}
                      disabled={currentJob?.status === "running"}
                      style={{ flex: 1 }}
                    >
                      {currentJob?.status === "running" ? "Generating…" : currentJob?.status === "done" ? "Regenerate" : "Generate Try-On"}
                    </button>
                    <button
                      className={isSaved(selectedProduct.id) ? "button button--secondary" : "button button--primary"}
                      onClick={() => toggleSave(selectedProduct.id)}
                      title={isSaved(selectedProduct.id) ? "Remove from saved" : "Save for later"}
                      style={{ minWidth: "44px" }}
                    >
                      {isSaved(selectedProduct.id) ? "★" : "☆"}
                    </button>
                  </div>

                  {/* Buy Now button */}
                  <a
                    href={"/checkout?product_id=" + selectedProduct.id + "&size=" + (selSize || selectedProduct.sizes[0] || "M") + "&color=" + encodeURIComponent(selectedProduct.colors[selColorIdx]?.name || "") + "&color_hex=" + encodeURIComponent(selectedProduct.colors[selColorIdx]?.hex || "") + "&fit=" + (selFit || "Regular")}
                    className="button button--primary"
                    style={{ display: "block", width: "100%", marginTop: "var(--sp-2)", textAlign: "center", textDecoration: "none" }}
                  >
                    Buy Now →
                  </a>
                </div>

                <div className="right-panel__section" style={{ flex: 1 }}>
                  <h4 className="right-panel__title" style={{ fontSize: "var(--fs-label)" }}>Result</h4>

                  {currentJob?.status === "running" && (
                    <div style={{ display: "flex", justifyContent: "center", padding: "var(--sp-8) 0" }}>
                      <ProgressRing progress={currentJob.progress} phase={currentJob.phase} />
                    </div>
                  )}

                  {currentJob?.status === "done" && currentJob.result && (
                    <>
                      <div className="tryon-stage__preview" style={{ marginBottom: "var(--sp-4)" }}>
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={currentJob.result.imageUrl} alt="Try-on result" />
                      </div>

                      {/* WhatsApp Share + Copy Link */}
                      <div style={{ display: "flex", gap: "var(--sp-2)", marginBottom: "var(--sp-4)" }}>
                        {waShareUrl && (
                          <a
                            href={waShareUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="wa-share-btn"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "var(--sp-2)",
                              flex: 1,
                              background: "#25D366",
                              color: "#fff",
                              borderRadius: "var(--radius)",
                              padding: "10px var(--sp-4)",
                              fontWeight: 600,
                              fontSize: "var(--fs-small)",
                              textDecoration: "none",
                            }}
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                            Share on WhatsApp
                          </a>
                        )}
                        <button
                          onClick={copyShareLink}
                          className="button button--secondary"
                          style={{ minWidth: "44px", padding: "0 var(--sp-3)" }}
                          title="Copy link"
                        >
                          {copied ? "✓" : "🔗"}
                        </button>
                      </div>

                      {/* Fit Breakdown */}
                      {currentJob.result.fitReport && (() => {
                        const report = currentJob.result.fitReport as any;
                        if (!report.rows) return null;
                        return (
                          <div className="fit-breakdown">
                            <div className="fit-breakdown__header">
                              <span>Measurement</span>
                              <span>Diff</span>
                              <span>Verdict</span>
                            </div>
                            {report.rows.map((row: any, i: number) => (
                              <div key={i} className="fit-breakdown__row">
                                <span>{row.label}</span>
                                <span>{row.differenceCm > 0 ? "+" : ""}{row.differenceCm?.toFixed(1)}cm</span>
                                <span className={`fit-breakdown__verdict ${
                                  row.verdict?.toLowerCase().includes("close") || row.verdict?.toLowerCase().includes("good")
                                    ? "fit-breakdown__verdict--good"
                                    : row.verdict?.toLowerCase().includes("very")
                                    ? "fit-breakdown__verdict--bad"
                                    : "fit-breakdown__verdict--warn"
                                }`}>
                                  {row.verdict}
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  )}

                  {currentJob?.status === "error" && (
                    <div className="tryon-stage__empty">
                      <div className="tryon-stage__empty-title">Generation Failed</div>
                      <div className="tryon-stage__empty-text">{currentJob.error}</div>
                    </div>
                  )}

                  {(!currentJob || currentJob.status === "idle") && (
                    <div className="tryon-stage__empty">
                      <div className="tryon-stage__empty-title">No Try-On Yet</div>
                      <div className="tryon-stage__empty-text">
                        Select your size, color, and fit preferences, then click Generate to see how this item looks on you.
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="right-panel__section">
                <div className="tryon-stage__empty">
                  <div className="tryon-stage__empty-title">Select a Product</div>
                  <div className="tryon-stage__empty-text">
                    Click any product from the catalog to start a virtual try-on.
                  </div>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* Mobile nav */}
      <nav className="mobile-nav">
        <Link href="/profile" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="10" cy="7" r="3" /><path d="M4 17c0-3 3-5 6-5s6 2 6 5" />
          </svg>
          Profile
        </Link>
        <Link href="/store" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="6" height="6" /><rect x="11" y="3" width="6" height="6" /><rect x="3" y="11" width="6" height="6" /><rect x="11" y="11" width="6" height="6" />
          </svg>
          Stores
        </Link>
        <Link href="/catalog" className="mobile-nav__btn mobile-nav__btn--active">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="6" height="6" /><rect x="11" y="3" width="6" height="6" /><rect x="3" y="11" width="6" height="6" /><rect x="11" y="11" width="6" height="6" />
          </svg>
          Catalog
        </Link>
        <Link href="/saved" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M5 3h10v14l-5-3-5 3V3z" />
          </svg>
          Saved
        </Link>
      </nav>
    </>
  );
}
